import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

// ── Shared server-side list pagination/search/filter/sort ──────────────────────
// Drives Next/Prev cursor pagination plus server-side search, filter and sort for
// any GraphQL connection. Entity hooks supply `fetchPage`, which builds their typed
// variables from the current state and returns the connection.

/** A relay-style connection page, trimmed to what the list UI needs. */
export interface PagedConnection<T> {
	nodes: T[];
	totalCount: number;
	pageInfo: {
		hasNextPage: boolean;
		hasPreviousPage: boolean;
		startCursor?: string | null;
		endCursor?: string | null;
	};
}

/** Cursor window passed to the server: forward (first/after) or backward (last/before). */
export interface PageWindow {
	first?: number;
	after?: string | null;
	last?: number;
	before?: string | null;
}

export interface PagedListState {
	debouncedSearch: string;
	filters: Record<string, string | undefined>;
	sort: SortingState;
	window: PageWindow;
}

interface UsePagedListOptions<TRow> {
	/** Stable key parts identifying the list (e.g. ["tasks", workspaceId]). */
	baseKey: unknown[];
	enabled?: boolean;
	pageSize?: number;
	initialSort?: SortingState;
	/** Pre-populates the search box (e.g. from a `?search=` route param). */
	initialSearch?: string;
	/** Pre-selects filters (e.g. from a `?status=` route param). */
	initialFilters?: Record<string, string | undefined>;
	/** How long a page stays fresh before a refetch is considered due (ms). Defaults to React
	 * Query's own default (0 — always stale) when omitted; set higher for a list backed by a live
	 * external call that doesn't need per-focus freshness (e.g. a rate-limited third-party API). */
	staleTime?: number;
	/** Defaults to React Query's own default (refetch) when omitted. */
	refetchOnWindowFocus?: boolean;
	fetchPage: (state: PagedListState) => Promise<PagedConnection<TRow>>;
}

/**
 * Builds a HotChocolate `where` from a search string (OR of `contains` over `searchFields`) plus
 * exact-match `eq` filters. Returns a generic object; callers cast to their *FilterInput type.
 */
export function buildWhere(
	search: string,
	searchFields: string[],
	filters: Record<string, string | undefined>,
): Record<string, unknown> | undefined {
	const and: Record<string, unknown>[] = [];
	const q = search.trim();
	if (q && searchFields.length) {
		and.push({ or: searchFields.map((f) => ({ [f]: { contains: q } })) });
	}
	for (const [key, value] of Object.entries(filters)) {
		if (value) and.push({ [key]: { eq: value } });
	}
	return and.length ? { and } : undefined;
}

/** SortingState → GraphQL `order` input (single column), or undefined. Caller casts to its input type. */
export function toOrder<T>(sort: SortingState): T[] | undefined {
	if (!sort.length) return undefined;
	const { id, desc } = sort[0];
	return [{ [id]: desc ? "DESC" : "ASC" } as unknown as T];
}

// CardViewer sort option value ("newest"/"oldest"/"name") ↔ SortingState. Shared by card routes.
export const keyToSort: Record<string, SortingState> = {
	newest: [{ id: "createdAt", desc: true }],
	oldest: [{ id: "createdAt", desc: false }],
	name: [{ id: "name", desc: false }],
};

export function cardSortToKey(sort: SortingState): string {
	const c = sort[0];
	if (!c) return "newest";
	if (c.id === "name") return "name";
	return c.desc ? "newest" : "oldest";
}

function useDebouncedValue<T>(value: T, delayMs = 300): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(t);
	}, [value, delayMs]);
	return debounced;
}

export function usePagedList<TRow>({
	baseKey,
	enabled = true,
	pageSize = 50,
	initialSort = [],
	initialSearch,
	initialFilters,
	staleTime,
	refetchOnWindowFocus,
	fetchPage,
}: UsePagedListOptions<TRow>) {
	const firstPage: PageWindow = useMemo(
		() => ({ first: pageSize }),
		[pageSize],
	);

	const [search, setSearchRaw] = useState(initialSearch ?? "");
	const [filters, setFilters] = useState<Record<string, string | undefined>>(
		initialFilters ?? {},
	);
	const [sort, setSortRaw] = useState<SortingState>(initialSort);
	const [window, setWindow] = useState<PageWindow>(firstPage);
	// 0-based index of the current page, for "Page X of Y" footers.
	const [pageIndex, setPageIndex] = useState(0);

	const debouncedSearch = useDebouncedValue(search);

	// Any search/filter/sort change invalidates the cursor — jump back to page 1.
	const setSearch = (v: string) => {
		setSearchRaw(v);
		setWindow(firstPage);
		setPageIndex(0);
	};
	const setFilter = (key: string, value: string | undefined) => {
		setFilters((prev) => ({ ...prev, [key]: value || undefined }));
		setWindow(firstPage);
		setPageIndex(0);
	};
	const setSort = (next: SortingState) => {
		setSortRaw(next);
		setWindow(firstPage);
		setPageIndex(0);
	};

	const state: PagedListState = { debouncedSearch, filters, sort, window };

	const query = useQuery({
		queryKey: [...baseKey, debouncedSearch, filters, sort, window],
		queryFn: () => fetchPage(state),
		enabled,
		placeholderData: keepPreviousData,
		staleTime,
		refetchOnWindowFocus,
	});

	const conn = query.data;
	const pageInfo = conn?.pageInfo;

	return {
		rows: (conn?.nodes ?? []).filter((n): n is NonNullable<TRow> => n != null),
		totalCount: conn?.totalCount ?? 0,
		hasNextPage: pageInfo?.hasNextPage ?? false,
		hasPreviousPage: pageInfo?.hasPreviousPage ?? false,
		pageIndex,
		pageSize,
		onNextPage: () => {
			setWindow({ first: pageSize, after: pageInfo?.endCursor });
			setPageIndex((p) => p + 1);
		},
		onPreviousPage: () => {
			setWindow({ last: pageSize, before: pageInfo?.startCursor });
			setPageIndex((p) => Math.max(0, p - 1));
		},
		loading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError,
		refetch: query.refetch,
		search,
		setSearch,
		filters,
		setFilter,
		sort,
		setSort,
	};
}

export type PagedList<TRow> = ReturnType<typeof usePagedList<TRow>>;
